import { defineStore } from 'pinia';
import { useHeadGenerate } from '~/composables/useHeadGenerate';
import {
  createPopulateString,
  parseItemInCollection,
} from '~/utils/strapiComponents/addStrapiData';
import { findIfPassesByGroups } from '~/utils/strapiComponents/filters';
import { useStoreRoot } from '../abstract/root';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';
import type { ISlotThemeData, ISlotThemeStrapiResponse } from '~/types/slots';

export const parseSlotTheme = (itemData: ISlotThemeData, page: boolean = true): ISlotThemeData => {
  const geo = useStoreRoot().geo;

  const item: ISlotThemeData = parseItemInCollection(JSON.parse(JSON.stringify(itemData)));

  if (geo && geo !== 'main') {
    const mirrorHead = item.mirrorHead?.find((head) => head?.geo === geo);
    const { getMirrorCanonical } = useHeadGenerate();

    if (mirrorHead) {
      item.descriptionHiddenText = '';
      item.descriptionText = '';
      item.head.title = mirrorHead.title;
      item.head.description = mirrorHead.description || '';
      item.head.nofollow = mirrorHead.nofollow || false;
      item.head.noindex = mirrorHead.noindex || false;
      item.head.canonical = getMirrorCanonical(mirrorHead);

      if (mirrorHead.noindex || mirrorHead.nofollow) {
        item.head.alternates = [];
      }
    }

    const mirrorBody = item.mirrorBody?.find((body) => body?.geo === geo);

    if (mirrorBody) {
      item.descriptionText = mirrorBody.descriptionText;
    }
  }

  item.isPage = page;

  return item;
};

export const useStoreSlotTheme = defineStore('slotTheme', {
  state: () => ({
    slotThemes: [] as ISlotThemeData[],
    requestPromise: null as Promise<ISlotThemeStrapiResponse> | null,
  }),

  getters: {
    list(state) {
      return (
        state.slotThemes?.slice().sort((a, b) => (a.position - b.position > -1 ? 1 : -1)) || []
      );
    },
    activeList(): ISlotThemeData[] {
      const storeRoot = useStoreRoot();

      return this.list
        .filter((item) => {
          return !!item.showInSelector;
        })
        .filter((item) => item.locale === storeRoot.userLang)
        .filter((item) => {
          if (!useStoreSsPlayer().isPlayerAuthorised) {
            if (item.groupFilter?.hideForGuests) {
              return false;
            }

            return true;
          }

          return findIfPassesByGroups(
            item?.groupFilter?.includedGroups || '',
            item?.groupFilter?.excludedGroups || ''
          );
        });
    },
    populateString() {
      return createPopulateString([
        'image',
        'head',
        'head.alternates',
        'mirrorHead',
        'mirrorBody',
        'groupFilter',
        'exploreMoreCollectionImage',
      ]);
    },
  },

  actions: {
    async loadSlotThemes() {
      if (!this.requestPromise) {
        this.requestPromise = strapi()
          .get('slot-themes-short')
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        this.ADD_DATA_FROM_STRAPI(response.data);

        return true;
      } else {
        return false;
      }
    },
    ADD_DATA_FROM_STRAPI(data: ISlotThemeData[]) {
      const parsedData = data
        .map((item) => (item.categoryId ? parseSlotTheme(item) : ({} as ISlotThemeData)))
        .filter(
          (parsedItem) =>
            !this.slotThemes.some(
              ({ categoryId }) => categoryId === parsedItem.categoryId && parsedItem.isPage
            )
        );

      const filteredState = this.slotThemes.filter(
        (item) => !parsedData.find(({ categoryId }) => categoryId && categoryId === item.categoryId)
      );

      this.slotThemes = [...filteredState, ...parsedData];
    },
    ADD_OR_EDIT_ONE_FROM_STRAPI(page: ISlotThemeData) {
      const parsedCategory = parseSlotTheme(page, true);
      const index = this.slotThemes.findIndex((p) => p.categoryId === parsedCategory.categoryId);

      if (index > -1) {
        this.slotThemes.splice(index, 1, parsedCategory);
      } else {
        this.slotThemes.push(parsedCategory);
      }
    },
  },
});
